import { useLayoutEffect, useRef, useState } from 'react';
import { TextStyle } from '../types/styles';

export type TFontVariant = 'display1' | 'display2' | 'header' | 'main' | 'secondary';

const DEFAULT_VARIANTS: TFontVariant[] = ['display1', 'display2', 'header', 'main', 'secondary'];

const defaultGetConstraintElement = (el: HTMLElement) => el.parentElement;

export interface IAutoResizeOptions {
    /** Начальный (максимальный) размер текста. По умолчанию 'display1' */
    initialVariant?: TFontVariant;
    /** Массив вариантов размеров для перебора в порядке уменьшения. По умолчанию весь список */
    variants?: readonly TFontVariant[];
    /** Функция для явного получения ограничивающего стабильного контейнера. По умолчанию возвращает immediate parent. */
    getConstraintElement?: (element: HTMLElement) => HTMLElement | null;
}

/**
 * Хук для автоматического подбора размера шрифта текста в одну строку с пост-валидацией.
 *
 * @param elementRef Ссылка (Ref) на HTML элемент или контейнер, содержащий целевой текстовый элемент первым потомком
 * @param content Содержимое текста (для отслеживания изменений)
 * @param options Настройки подбора размеров и поиска контейнера-ограничителя
 */
export const useAutoResizeText = <T extends HTMLElement>(
    elementRef: React.RefObject<T | null>,
    content: string,
    options: IAutoResizeOptions = {},
): TFontVariant => {
    const { initialVariant = 'display1', variants = DEFAULT_VARIANTS, getConstraintElement = defaultGetConstraintElement } = options;

    const [effectiveVariant, setEffectiveVariant] = useState<TFontVariant>(initialVariant);

    // Кэш для отслеживания реальных изменений контента или размеров родителя
    const cacheRef = useRef<{ content: string; parentWidth: number } | null>(null);

    // Сохраняем и синхронизируем коллбек в Ref, чтобы избежать перезапуска основного эффекта
    // на каждом рендере из-за новой идентичности инлайновых функций-коллбеков.
    // Синхронизацию делаем строго в useLayoutEffect для соблюдения чистоты фазы рендеринга (pure render phase).
    const constraintCallbackRef = useRef(getConstraintElement);

    useLayoutEffect(() => {
        constraintCallbackRef.current = getConstraintElement;
    }, [getConstraintElement]);

    useLayoutEffect(() => {
        // Получаем целевой элемент: либо сам элемент из ref, либо его первый дочерний элемент (если обернут)
        const element = (elementRef.current?.firstElementChild || elementRef.current) as HTMLElement;
        if (!element || !content) {
            return;
        }

        // Получаем ограничивающий контейнер на основе функции обратного вызова или родителя по умолчанию
        const constraintElement = constraintCallbackRef.current(element) || element;

        // Считываем точные (дробные) размеры ограничивающего предка через getBoundingClientRect().width
        const containerStyle = window.getComputedStyle(constraintElement);
        const sPaddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
        const sPaddingRight = parseFloat(containerStyle.paddingRight) || 0;
        const sBorderLeft = parseFloat(containerStyle.borderLeftWidth) || 0;
        const sBorderRight = parseFloat(containerStyle.borderRightWidth) || 0;

        let availableWidth = constraintElement.getBoundingClientRect().width - sPaddingLeft - sPaddingRight - sBorderLeft - sBorderRight;

        // В тестовом окружении JSDOM getBoundingClientRect().width часто равен 0, поэтому делаем фоллбек на clientWidth
        if (availableWidth <= 0) {
            availableWidth = constraintElement.clientWidth - sPaddingLeft - sPaddingRight - sBorderLeft - sBorderRight;
        }

        // Автоматически вычисляем и вычитаем паддинги, маргины и бордеры всех промежуточных врапперов
        // между element и ограничивающим constraintElement. Это гарантирует 100% точность замеров при любой вложенности DOM.
        let currentParent = element.parentElement;
        while (currentParent && currentParent !== constraintElement && currentParent !== document.body) {
            const cpStyle = window.getComputedStyle(currentParent);
            const cpPaddingLeft = parseFloat(cpStyle.paddingLeft) || 0;
            const cpPaddingRight = parseFloat(cpStyle.paddingRight) || 0;
            const cpMarginLeft = parseFloat(cpStyle.marginLeft) || 0;
            const cpMarginRight = parseFloat(cpStyle.marginRight) || 0;
            const cpBorderLeft = parseFloat(cpStyle.borderLeftWidth) || 0;
            const cpBorderRight = parseFloat(cpStyle.borderRightWidth) || 0;

            availableWidth -= cpPaddingLeft + cpPaddingRight + cpMarginLeft + cpMarginRight + cpBorderLeft + cpBorderRight;
            currentParent = currentParent.parentElement;
        }

        // Если доступная ширина все еще 0 (элемент еще не примонтирован или скрыт), выходим
        if (availableWidth <= 0) {
            return;
        }

        // Проверяем, изменились ли данные (текст или ширина) с прошлого замера
        const isDataChanged = !cacheRef.current || cacheRef.current.content !== content || cacheRef.current.parentWidth !== availableWidth;

        // Обновляем кэш
        cacheRef.current = { content, parentWidth: availableWidth };

        // 1. Пост-валидация: Проверяем, помещается ли текущий реально примененный шрифт в DOM.
        // Обязательно вычитаем вертикальные паддинги и бордеры из scrollHeight.
        const currentStyle = window.getComputedStyle(element);
        const currentPaddingTop = parseFloat(currentStyle.paddingTop) || 0;
        const currentPaddingBottom = parseFloat(currentStyle.paddingBottom) || 0;
        const currentBorderTop = parseFloat(currentStyle.borderTopWidth) || 0;
        const currentBorderBottom = parseFloat(currentStyle.borderBottomWidth) || 0;

        const currentContentHeight = element.scrollHeight - currentPaddingTop - currentPaddingBottom - currentBorderTop - currentBorderBottom;

        let currentLineHeight = parseFloat(currentStyle.lineHeight);
        if (isNaN(currentLineHeight) || currentLineHeight <= 0) {
            const fontSize = parseFloat(currentStyle.fontSize);
            currentLineHeight = isNaN(fontSize) || fontSize <= 0 ? 16 : fontSize * 1.2;
        }

        const isCurrentlyFitting = Math.round(currentContentHeight / currentLineHeight) <= 1;

        // Определяем индекс, с которого начнем подбор вариантов.
        let startIndex = 0;
        if (!isDataChanged) {
            if (isCurrentlyFitting) {
                // Если контент не менялся и он помещается — ничего делать не нужно, выходим.
                return;
            }
            // Если не помещается, начинаем поиск вниз с текущего размера
            const currentVariantIndex = variants.indexOf(effectiveVariant);
            if (currentVariantIndex !== -1) {
                startIndex = currentVariantIndex;
            }
        }

        // Создаем клон для невидимого измерения
        const measurementNode = element.cloneNode(true) as HTMLElement;

        // Настраиваем клон вне экрана с контентной шириной оригинального элемента.
        // Сбрасываем все вертикальные padding, margin и border в 0, чтобы scrollHeight
        // клона возвращал исключительно чистую высоту текстового контейнера.
        // Добавляем 1px погрешности (subpixel tolerance), чтобы избежать ложных переносов
        // из-за дробных погрешностей рендеринга и округления.
        Object.assign(measurementNode.style, {
            position: 'absolute',
            top: '0',
            visibility: 'hidden',
            pointerEvents: 'none',
            height: 'auto',
            width: `${availableWidth + 1}px`,
            display: 'block', // сбрасываем флексы/гриды для точного скролла
            paddingTop: '0px',
            paddingBottom: '0px',
            marginTop: '0px',
            marginBottom: '0px',
            borderTopWidth: '0px',
            borderBottomWidth: '0px',
            borderStyle: 'none',
        });

        // Добавляем клон в родительский элемент оригинального узла, чтобы он наследовал
        // все CSS-переменные, шрифты и тему (например, .theme-dark, которая задана на предках).
        const mountTarget = element.parentElement || document.body;
        mountTarget.appendChild(measurementNode);

        let bestFitVariant = variants[startIndex] || initialVariant;

        // Перебираем варианты от startIndex до конца массива
        for (let i = startIndex; i < variants.length; i++) {
            const testVariant = variants[i];
            const styleConfig = TextStyle[testVariant];

            // Применяем семейство шрифта
            if (styleConfig.font) {
                measurementNode.style.setProperty('--cw-txt-font-family', styleConfig.font);
            }

            // Применяем все параметры стиля шрифта из TextStyle
            if (styleConfig.fontStyle) {
                const { fontSize, fontWeight, lineHeight, letterSpacing } = styleConfig.fontStyle;

                if (fontSize) {
                    measurementNode.style.setProperty('--cw-txt-font-size', `${fontSize}`);
                }
                if (fontWeight) {
                    measurementNode.style.setProperty('--cw-txt-font-weight', typeof fontWeight === 'number' ? fontWeight.toString() : `${fontWeight}`);
                }
                if (lineHeight) {
                    measurementNode.style.setProperty('--cw-txt-line-height', `${lineHeight}`);
                }
                if (letterSpacing) {
                    measurementNode.style.setProperty('--cw-txt-letter-spacing', `${letterSpacing}`);
                }
            }

            // Измеряем высоту строки клонированного узла с новыми стилями
            const computedStyle = window.getComputedStyle(measurementNode);
            let computedLineHeight = parseFloat(computedStyle.lineHeight);

            if (isNaN(computedLineHeight) || computedLineHeight <= 0) {
                const fontSize = parseFloat(computedStyle.fontSize);
                computedLineHeight = isNaN(fontSize) || fontSize <= 0 ? 16 : fontSize * 1.2;
            }

            // Проверяем помещается ли в 1 строку (так как паддинги клона сброшены в 0,
            // scrollHeight равен чистой высоте букв).
            if (computedLineHeight > 0 && Math.round(measurementNode.scrollHeight / computedLineHeight) <= 1) {
                bestFitVariant = testVariant;
                break;
            }
            bestFitVariant = testVariant;
        }

        // Чистим DOM после измерений
        mountTarget.removeChild(measurementNode);

        // Синхронно обновляем состояние ДО отрисовки страницы браузером (pre-paint),
        // чтобы предотвратить скачки интерфейса и вспышки текста некорректного размера.
        // Локально подавляем правило eslint, так как это является задокументированным и
        // стандартным паттерном использования useLayoutEffect для верстки.
        if (bestFitVariant !== effectiveVariant) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setEffectiveVariant(bestFitVariant);
        }
    }, [content, elementRef, initialVariant, variants, effectiveVariant]);

    return effectiveVariant;
};
